import { Controller, useFormContext } from 'react-hook-form'
import { useReadMunicipalities } from '@/api/accounting-dealer/person'
import { CepResponse } from '@/types'
import { states } from '@/constants/states'
import { Stack } from '@mui/material'
import { CepSearchField, type CepSearchFieldProps } from './cep-search-field'
import { useMemo } from 'react'
import { AutocompleteInput, TextInput } from '../inputs'

export function AddressForm({
  cepField,
  disabled,
}: {
  cepField?: Partial<CepSearchFieldProps>
  disabled?: boolean
}) {
  const { control, setValue, watch, clearErrors } = useFormContext()

  const [stateCode] = watch(['address.state_code'])

  const { data: municipalities, isLoading: readingMunicipalities } =
    useReadMunicipalities(stateCode)

  const handleFindCep = (cep: CepResponse) => {
    setValue('address.street', cep.street)
    setValue('address.ibge_code', cep.ibge_code)
    setValue('address.state_code', cep.state_code)
    setValue('address.district', cep.district)

    clearErrors('address')
  }

  const municipalityOptions = useMemo(
    () => municipalities?.map((municipality) => municipality.ibge_code) || [],
    [municipalities],
  )

  return (
    <>
      <Controller
        name="address.zip_code"
        defaultValue={''}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <CepSearchField
            label={'CEP'}
            sx={{ width: { sm: '100%', md: 200 } }}
            {...cepField}
            disabled={disabled}
            error={Boolean(error)}
            helperText={error?.message}
            onFindCep={handleFindCep}
            {...field}
          />
        )}
      />

      <Stack direction={{ sm: 'column', md: 'row' }} gap={2}>
        <TextInput
          control={control}
          name="address.street"
          label="Rua"
          disabled={disabled}
        />

        <TextInput
          control={control}
          name="address.number"
          label="Número"
          disabled={disabled}
          sx={{ width: { sm: '100%', md: 140 } }}
        />
      </Stack>

      <Stack direction={{ sm: 'column', md: 'row' }} gap={2}>
        <TextInput
          control={control}
          name="address.complement"
          label="Complemento"
          disabled={disabled}
        />

        <TextInput
          control={control}
          name="address.district"
          label="Bairro"
          disabled={disabled}
        />
      </Stack>

      <Stack direction={{ sm: 'column', md: 'row' }} gap={2}>
        <AutocompleteInput
          control={control}
          name="address.state_code"
          label="Estado"
          options={states.map((state) => state.code)}
          disabled={disabled}
        />

        <AutocompleteInput
          control={control}
          name="address.ibge_code"
          label="Cidade"
          options={municipalityOptions}
          disabled={disabled}
          getOptionLabel={(option) =>
            municipalities?.find((item) => item.ibge_code === option)?.name ??
            ''
          }
          loading={readingMunicipalities}
        />
      </Stack>
    </>
  )
}
